import { useEffect, useCallback, useRef, useMemo } from 'react';
import type { MarkerInstance } from 'src/types/map-provider';
import type { UseMapMarkersOptions, UseMapMarkersReturn } from 'src/types/map-hooks';

const isValidLatLng = (position: { lat: number; lng: number } | undefined): boolean =>
  position != null &&
  Number.isFinite(position.lat) &&
  Number.isFinite(position.lng);

/**
 * Hook for managing multiple map markers
 *
 * Fully automated hook that handles:
 * - Creating/destroying markers when markers array changes
 * - Updating marker animations based on highlightedMarkerId
 * - Updating marker icons based on selectedIds
 * - Auto-fitting map bounds or centering based on marker count
 * - Attaching click and drag event listeners
 *
 * @example
 * ```tsx
 * const { markerInstances } = useMapMarkers({
 *   provider,
 *   mapInstance,
 *   markers: [{ position: { lat: 40, lng: -74 }, title: 'NYC' }],
 *   highlightedMarkerId: 1,
 *   selectedIds: [1, 2],
 *   onMarkerClick: (index) => console.log('Clicked:', index),
 * });
 * ```
 *
 * @param options - Configuration options
 * @returns Marker instances and control functions
 */
export function useMapMarkers(options: UseMapMarkersOptions): UseMapMarkersReturn {
  const {
    provider,
    mapInstance,
    markers = [],
    viewOnly = false,
    initialLocation,
    highlightedMarkerId,
    selectedIds,
    onMarkerClick,
    onMarkerDragEnd,
    t = (key: string) => key, // Default fallback
  } = options;

  // Ref to track marker instances
  const markerInstancesRef = useRef<MarkerInstance[]>([]);

  // Clear all markers
  const clearMarkers = useCallback(() => {
    markerInstancesRef.current.forEach((marker) => {
      if (marker && marker.setVisible) {
        marker.setVisible(false);
      }
      if (marker && marker.destroy) {
        marker.destroy();
      }
    });
    markerInstancesRef.current = [];
  }, []);

  // Effect 1: Main markers creation and update
  useEffect(() => {
    if (!mapInstance || !provider) return;

    // Clear existing markers
    clearMarkers();

    if (markers.length > 0) {
      // Create markers from markers array
      markers.forEach((marker, index) => {
        if (!isValidLatLng(marker.position)) {
          return;
        }

        const isInspectorMarker = 'id' in marker;
        const isSelected = isInspectorMarker && selectedIds?.includes((marker as any).id);
        const isHighlighted = isInspectorMarker && highlightedMarkerId === (marker as any).id;

        // Determine marker icon
        const markerIcon = isInspectorMarker
          ? {
              url: `https://api.iconify.design/material-symbols:home-pin-rounded.svg?color=%23${
                isSelected ? '000000' : '4285F4'
              }&width=32&height=32`,
            }
          : marker.icon;

        // Create the marker
        const newMarker = provider.createMarker({
          map: mapInstance,
          position: marker.position,
          draggable: marker.draggable ?? false,
          title: marker.title || (isInspectorMarker ? 'Inspector location' : 'Location'),
          animation: isHighlighted ? 'bounce' : undefined,
          icon: markerIcon,
        });

        if (newMarker) {
          // Attach click listener
          if (marker.onClick || onMarkerClick) {
            newMarker.addListener('click', () => {
              marker.onClick?.();
              onMarkerClick?.(index);
            });
          }

          // Attach dragend listener
          if (marker.draggable && onMarkerDragEnd) {
            newMarker.addListener('dragend', () => {
              const position = newMarker.getPosition();
              if (position) {
                onMarkerDragEnd(index, position);
              }
            });
          }

          markerInstancesRef.current[index] = newMarker;
        }
      });

      const mappableMarkers = markers.filter((marker) => isValidLatLng(marker.position));

      // Auto-fit bounds or center map
      if (mappableMarkers.length > 1) {
        const bounds = provider.createBounds();
        mappableMarkers.forEach((marker) => bounds.extend(marker.position));
        mapInstance.fitBounds(bounds);
      } else if (mappableMarkers.length === 1) {
        mapInstance.setCenter(mappableMarkers[0].position);
      }
    } else if (!viewOnly && initialLocation) {
      const marker = provider.createMarker({
        map: mapInstance,
        position: initialLocation,
        draggable: true,
        title: t('dragToUpdate'),
      });

      if (marker && onMarkerDragEnd) {
        marker.addListener('dragend', () => {
          const position = marker.getPosition();
          if (position) {
            onMarkerDragEnd(0, position);
          }
        });

        markerInstancesRef.current[0] = marker;
      }

      // Center on initial location
      mapInstance.setCenter(initialLocation);
      mapInstance.setZoom(17);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      clearMarkers();
    };
  }, [
    mapInstance,
    provider,
    markers,
    viewOnly,
    initialLocation,
    onMarkerClick,
    onMarkerDragEnd,
    t,
    clearMarkers,
    highlightedMarkerId,
    selectedIds,
  ]);

  // Effect 2: Update marker animations when highlightedMarkerId changes
  useEffect(() => {
    if (!mapInstance || markers.length === 0) return;

    markerInstancesRef.current.forEach((marker, index) => {
      if (marker && markers[index] && 'id' in markers[index]) {
        const isHighlighted = highlightedMarkerId === (markers[index] as any).id;
        if (marker.setAnimation) {
          marker.setAnimation(isHighlighted ? 'bounce' : null);
        }
      }
    });
  }, [highlightedMarkerId, markers, mapInstance]);

  // Effect 3: Update marker icons when selectedIds changes
  useEffect(() => {
    if (!mapInstance || markers.length === 0) return;

    markerInstancesRef.current.forEach((marker, index) => {
      if (marker && markers[index] && 'id' in markers[index]) {
        const isSelected = selectedIds?.includes((markers[index] as any).id);
        if (marker.setIcon) {
          marker.setIcon({
            url: `https://api.iconify.design/material-symbols:home-pin-rounded.svg?color=%23${
              isSelected ? '000000' : '4285F4'
            }&width=32&height=32`,
          });
        }
      }
    });
  }, [selectedIds, markers, mapInstance]);

  // Return memoized value
  const memoizedValue = useMemo<UseMapMarkersReturn>(
    () => ({
      markerInstances: markerInstancesRef.current,
      clearMarkers,
    }),
    [clearMarkers]
  );

  return memoizedValue;
}
